import type { BuildingProgram, Parcel } from '../config.js';

/** Functional building classification — the primary taxonomy archetypes place by position. */
export type BuildingFunction =
  | 'command' | 'civic' | 'habitation' | 'habitation_luxury'
  | 'commerce' | 'market' | 'medical' | 'science'
  | 'agriculture' | 'industry' | 'storage' | 'manufacturing'
  | 'utility' | 'defense' | 'communication' | 'transit'
  | 'landing' | 'recreation' | 'park'
  // Spec 2 — ground archetypes (Spaceport / Cyberpunk / Dome Colony)
  | 'docking' | 'customs' | 'repair' | 'fuel'
  | 'corporate' | 'datacenter' | 'entertainment'
  | 'airlock' | 'shield'
  // Spec 3 — exotic archetypes (Orbital Citadel / Arcology Spire / Ring Station)
  | 'barracks' | 'armory' | 'reactor' | 'lifesupport' | 'concourse';

export const BUILDING_FUNCTIONS: readonly BuildingFunction[] = [
  'command', 'civic', 'habitation', 'habitation_luxury',
  'commerce', 'market', 'medical', 'science',
  'agriculture', 'industry', 'storage', 'manufacturing',
  'utility', 'defense', 'communication', 'transit',
  'landing', 'recreation', 'park',
  'docking', 'customs', 'repair', 'fuel',
  'corporate', 'datacenter', 'entertainment',
  'airlock', 'shield',
  'barracks', 'armory', 'reactor', 'lifesupport', 'concourse',
] as const;

/** Derives one of the 7 generic mesh shapes (backward-compat for BuildingMeshFactory + Atlas). */
export const FUNCTION_TO_TYPE: Record<BuildingFunction, BuildingProgram['type']> = {
  command: 'office', civic: 'office', habitation: 'house', habitation_luxury: 'apartment',
  commerce: 'office', market: 'shop', medical: 'office', science: 'office',
  agriculture: 'warehouse', industry: 'factory', storage: 'warehouse', manufacturing: 'factory',
  utility: 'warehouse', defense: 'warehouse', communication: 'office', transit: 'office',
  landing: 'warehouse', recreation: 'shop', park: 'house', // 'park' never built (policy returns null)
  // Spec 2 ground archetypes
  docking: 'warehouse', customs: 'office', repair: 'factory', fuel: 'warehouse',
  corporate: 'skyscraper', datacenter: 'warehouse', entertainment: 'shop',
  airlock: 'warehouse', shield: 'factory',
  // Spec 3 exotic archetypes
  barracks: 'apartment', armory: 'warehouse', reactor: 'factory', lifesupport: 'warehouse', concourse: 'shop',
};

/** Derives one of the 5 parcel zones (backward-compat for parcel.zone + mesh colors). */
export const FUNCTION_TO_ZONE: Record<BuildingFunction, Parcel['zone']> = {
  command: 'commercial', civic: 'commercial', habitation: 'residential', habitation_luxury: 'residential',
  commerce: 'commercial', market: 'commercial', medical: 'commercial', science: 'commercial',
  agriculture: 'industrial', industry: 'industrial', storage: 'industrial', manufacturing: 'industrial',
  utility: 'industrial', defense: 'industrial', communication: 'commercial', transit: 'commercial',
  landing: 'industrial', recreation: 'park', park: 'park',
  // Spec 2 ground archetypes
  docking: 'industrial', customs: 'commercial', repair: 'industrial', fuel: 'industrial',
  corporate: 'commercial', datacenter: 'industrial', entertainment: 'commercial',
  airlock: 'industrial', shield: 'industrial',
  // Spec 3 exotic archetypes
  barracks: 'residential', armory: 'industrial', reactor: 'industrial', lifesupport: 'industrial', concourse: 'commercial',
};
